import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Tag,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Empty,
  Collapse,
  Tooltip,
  Popconfirm,
  Badge
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import {
  MunicipalityAssignment,
  getAccessLevelLabel,
  isAssignmentValid
} from '@municipality/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface MunicipalityAccessListProps {
  assignments: (MunicipalityAssignment & { municipalityName?: string })[];
  loading?: boolean;
  onEdit: (assignment: MunicipalityAssignment) => void;
  onRevoke: (municipalityId: string, municipalityName?: string) => void;
  onResend: (municipalityId: string, municipalityName?: string) => void;
}

const MunicipalityAccessList: React.FC<MunicipalityAccessListProps> = ({
  assignments,
  loading = false,
  onEdit,
  onRevoke,
  onResend
}) => {
  const { t } = useTranslation('users');

  if (assignments.length === 0 && !loading) {
    return (
      <Card>
        <Empty
          description={t('noMunicipalityAssignments', 'No municipality assignments')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  const getAccessLevelColor = (level: string): string => {
    switch (level) {
      case 'none':
        return 'default';
      case 'read_only':
        return 'blue';
      case 'content_manager':
        return 'cyan';
      case 'full_access':
        return 'green';
      default:
        return 'default';
    }
  };

  const renderPermissions = (assignment: MunicipalityAssignment) => {
    const permissions = assignment.permissions;
    const enabledPermissions = Object.entries(permissions)
      .filter(([, value]) => value === true)
      .map(([key]) => key);

    return (
      <Space wrap size="small">
        {enabledPermissions.slice(0, 5).map((perm) => (
          <Tag key={perm} color="blue" style={{ fontSize: 11 }}>
            {t(`permissions.${perm}`, perm)}
          </Tag>
        ))}
        {enabledPermissions.length > 5 && (
          <Tag color="default">+{enabledPermissions.length - 5} {t('more', 'more')}</Tag>
        )}
      </Space>
    );
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {assignments.map((assignment) => {
        const isValid = isAssignmentValid(assignment);
        const isExpired = assignment.expiresAt && dayjs(assignment.expiresAt).isBefore(dayjs());

        return (
          <Badge.Ribbon
            key={assignment.municipalityId}
            text={isExpired ? t('expired', 'Expired') : undefined}
            color={isExpired ? 'red' : undefined}
          >
            <Card
              hoverable
              style={{
                opacity: isValid ? 1 : 0.6,
                borderColor: isExpired ? '#ff4d4f' : undefined,
              }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={16}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space>
                      <Title level={5} style={{ margin: 0 }}>
                        {assignment.municipalityName || assignment.municipalityId}
                      </Title>
                      <Tag color={getAccessLevelColor(assignment.accessLevel)}>
                        {getAccessLevelLabel(assignment.accessLevel)}
                      </Tag>
                      {isValid ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      )}
                    </Space>

                    <Space direction="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <UserOutlined /> {t('grantedBy', 'Granted by')}: {assignment.grantedBy}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <CalendarOutlined /> {t('grantedAt', 'Granted at')}:{' '}
                        {dayjs(assignment.grantedAt).format('MMM D, YYYY HH:mm')}
                      </Text>
                      {assignment.expiresAt && (
                        <Text
                          type={isExpired ? 'danger' : 'secondary'}
                          style={{ fontSize: 12 }}
                        >
                          <ClockCircleOutlined />{' '}
                          {isExpired
                            ? t('expiredOn', 'Expired on')
                            : t('expiresOn', 'Expires on')}
                          : {dayjs(assignment.expiresAt).format('MMM D, YYYY')}
                          {' '}({dayjs(assignment.expiresAt).fromNow()})
                        </Text>
                      )}
                      {assignment.lastAccessed && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t('lastAccessed', 'Last accessed')}:{' '}
                          {dayjs(assignment.lastAccessed).fromNow()}
                        </Text>
                      )}
                    </Space>

                    {assignment.notes && (
                      <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                        {t('notes', 'Notes')}: {assignment.notes}
                      </Text>
                    )}

                    <Collapse ghost>
                      <Panel
                        header={t('viewPermissions', 'View permissions')}
                        key="permissions"
                        style={{ fontSize: 12 }}
                      >
                        {renderPermissions(assignment)}
                      </Panel>
                    </Collapse>
                  </Space>
                </Col>

                <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {isExpired && (
                      <Tooltip title={t('renewAssignment', 'Renew assignment')}>
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => onResend(assignment.municipalityId, assignment.municipalityName)}
                          block
                        >
                          {t('resend', 'Resend')}
                        </Button>
                      </Tooltip>
                    )}
                    
                    <Tooltip title={t('editAssignment', 'Edit assignment')}>
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => onEdit(assignment)}
                        disabled={!isValid}
                        block
                      >
                        {t('edit', 'Edit')}
                      </Button>
                    </Tooltip>

                    <Popconfirm
                      title={t('revokeAccessConfirm', 'Are you sure you want to revoke access?')}
                      description={t('revokeAccessDescription', 'This action cannot be undone.')}
                      onConfirm={() => onRevoke(assignment.municipalityId, assignment.municipalityName)}
                      okText={t('yes', 'Yes')}
                      cancelText={t('no', 'No')}
                      okButtonProps={{ danger: true }}
                    >
                      <Button icon={<DeleteOutlined />} danger block>
                        {t('revoke', 'Revoke')}
                      </Button>
                    </Popconfirm>
                  </Space>
                </Col>
              </Row>
            </Card>
          </Badge.Ribbon>
        );
      })}
    </Space>
  );
};

export default MunicipalityAccessList;
